import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  Alert,
  Paper,
  CircularProgress,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTenantTranslation } from "hooks/formatters";
import { useTenantData } from "contexts/tenant";
import { useNotifyStatus } from "contexts/status";
import { useTenant } from "hooks/settings";
import { useSaveAndParseFileMutation } from "src/store/api/generatedApi";
import { getBase64 } from "helpers";
import { getTodayStartInTenantLocalTime } from "./useRecentInventoryFiles";
import { useRecentInventoryReportFile } from "./useRecentInventoryReportFile";
import { FileUploadCard, FileUploadStatus } from "./FileUploadCard";
import type { UploadedFileType } from "components/file/upload/uploadedFileType";

const POLL_INTERVAL_MS = 3000;
const UPLOAD_PROCESSING_DELAY_MS = 2000;

interface InventoryReportUploadGatewayProps {
  children: React.ReactNode;
}

export const InventoryReportUploadGateway: React.FC<
  InventoryReportUploadGatewayProps
> = ({ children }) => {
  const { t } = useTenantTranslation();
  const notifyStatus = useNotifyStatus();
  const tenantName = useTenant();
  const [saveAndParseFile, { isLoading: isUploading }] =
    useSaveAndParseFileMutation();
  const { local_timezone: localTimezone } = useTenantData();

  const [showUploadInterface, setShowUploadInterface] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);

  const todayStart = getTodayStartInTenantLocalTime(localTimezone);

  const { fileStatus, isLoading, refetch } =
    useRecentInventoryReportFile(todayStart);

  const fileIsPresent = fileStatus.isPresent;
  const fileIsProcessed = fileStatus.isProcessed;

  useEffect(() => {
    if (!isLoading) {
      if (!fileIsPresent && !showUploadInterface) {
        // eslint-disable-next-line @eslint-react/set-state-in-effect -- upload flow state machine transition
        setShowUploadInterface(true);
      }
    }
  }, [isLoading, fileIsPresent, showUploadInterface]);

  useEffect(() => {
    if (!isPolling) return;

    const interval = setInterval(() => {
      refetch();
    }, POLL_INTERVAL_MS);

    return () => {
      clearInterval(interval);
    };
  }, [isPolling, refetch]);

  useEffect(() => {
    if (isPolling && fileIsProcessed) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- stops polling after async processing completes
      setIsPolling(false);
      notifyStatus({
        text: t("allFilesProcessed"),
        type: "success",
      });
    }
  }, [isPolling, fileIsProcessed, notifyStatus, t]);

  const handleFileDrop = useCallback(
    async (files: FileList) => {
      const file = files[0];
      if (!file) return;

      const fileContent = await getBase64(file);
      if (typeof fileContent !== "string") {
        notifyStatus({
          text: t("failedToUploadFile"),
          type: "error",
        });
        return;
      }

      saveAndParseFile({
        tenantName,
        uploadedFileCreate: {
          type: "inventory_report",
          file: {
            name: file.name,
            content: fileContent,
          },
        },
      })
        .unwrap()
        .then(() => {
          notifyStatus({
            text: `${t("uploadFileSuccess")}: ${file.name}`,
            type: "success",
          });
          setTimeout(() => {
            refetch();
            setIsPolling(true);
          }, UPLOAD_PROCESSING_DELAY_MS);
        })
        .catch(() => {
          notifyStatus({
            text: `${t("failedToUploadFile")}: ${file.name}`,
            type: "error",
          });
        });
    },
    [saveAndParseFile, tenantName, notifyStatus, refetch, t]
  );

  const handleProceedWithRecentFile = () => {
    if (fileIsProcessed) {
      setShowForm(true);
    } else {
      setIsPolling(true);
    }
  };

  const handleUploadNewFile = () => {
    setShowUploadInterface(true);
  };

  const handleContinueToForm = () => {
    if (!fileIsPresent || !fileIsProcessed) {
      setShowValidationError(true);
      return;
    }
    setShowForm(true);
  };

  const getUploadStatus = (): FileUploadStatus => {
    if (!fileStatus.isPresent) return "pending";
    if (fileStatus.isProcessed) return "processed";
    return "processing";
  };

  const isFileProcessing = fileIsPresent && !fileIsProcessed;

  if (showForm) {
    return children;
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 8,
          gap: 3,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          {t("loading")}
        </Typography>
      </Box>
    );
  }

  if (fileIsPresent && !showUploadInterface) {
    return (
      <Box sx={{ p: 3 }}>
        <Stack spacing={3} maxWidth="900px" mx="auto">
          <Typography variant="h4" gutterBottom>
            {t("uploadInventoryReport")}
          </Typography>

          <Alert severity="info">{t("recentFilesFound")}</Alert>

          <FileUploadCard
            fileType={"inventory_report" as UploadedFileType}
            file={fileStatus.file}
            status={getUploadStatus()}
          />

          <Stack direction="row" spacing={2} justifyContent="center">
            {fileIsProcessed ? (
              <Button
                variant="contained"
                size="large"
                startIcon={<CheckCircleIcon />}
                onClick={handleProceedWithRecentFile}
              >
                {t("continueToForm")}
              </Button>
            ) : (
              <LoadingButton
                variant="contained"
                size="large"
                onClick={handleProceedWithRecentFile}
                loading={isPolling}
              >
                {isPolling
                  ? t("waitingForProcessing")
                  : t("proceedWithRecentFiles")}
              </LoadingButton>
            )}
            <Button
              variant="outlined"
              size="large"
              onClick={handleUploadNewFile}
            >
              {t("uploadNewFiles")}
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3} maxWidth="900px" mx="auto">
        <Typography variant="h4" gutterBottom>
          {t("uploadInventoryReport")}
        </Typography>

        <Alert severity="info">{t("uploadInventoryReportRequired")}</Alert>

        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: "2px dashed",
            borderColor: "divider",
            backgroundColor: "background.default",
          }}
        >
          <Stack spacing={2}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CloudUploadIcon color="action" />
              <Typography variant="h6">{t("uploadFiles")}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {t("dragAndDropOrClick")}
            </Typography>
            <Box
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                void handleFileDrop(e.dataTransfer.files);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => {
                  if (e.target.files) {
                    void handleFileDrop(e.target.files);
                  }
                }}
                style={{ display: "none" }}
                id="inventory-report-upload-input"
              />
              <label htmlFor="inventory-report-upload-input">
                <Button
                  variant="outlined"
                  component="span"
                  fullWidth
                  disabled={isUploading}
                  startIcon={
                    isUploading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <CloudUploadIcon />
                    )
                  }
                >
                  {isUploading ? t("uploading") : t("selectFiles")}
                </Button>
              </label>
            </Box>
          </Stack>
        </Paper>

        <FileUploadCard
          fileType={"inventory_report" as UploadedFileType}
          file={fileStatus.file}
          status={getUploadStatus()}
        />

        {isFileProcessing ? (
          <Alert severity="info" icon={<CircularProgress size={20} />}>
            {t("waitingForProcessing")}
          </Alert>
        ) : null}

        {showValidationError && !fileIsPresent ? (
          <Alert severity="error">{t("inventoryReportRequired")}</Alert>
        ) : null}

        {showValidationError && fileIsPresent && !fileIsProcessed ? (
          <Alert severity="error">{t("waitingForProcessing")}</Alert>
        ) : null}

        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <LoadingButton
            variant="contained"
            size="large"
            onClick={handleContinueToForm}
            loading={isFileProcessing}
            startIcon={
              fileIsProcessed ? <CheckCircleIcon /> : <CloudUploadIcon />
            }
          >
            {t("continueToForm")}
          </LoadingButton>
        </Box>
      </Stack>
    </Box>
  );
};
